import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '../../store/store';
import { sendContactUs, resetContactUs, ContactUsData } from '../../store/slices/contactUsSlice';
import { addNotification } from '../../store/slices/notificationSlice';
import Input from '../ui/Input';
import Checkbox from '../ui/Checkbox';
import { formatPhoneNumber, cleanPhoneNumber } from '../../utils/phone_mask';
import { RecaptchaProvider, useRecaptchaToken } from '../../hooks/useRecaptcha';

interface ContactFormProps {
  licenseType: string;
  className?: string;
}

const ContactFormInner: React.FC<ContactFormProps> = ({ licenseType, className = '' }) => {
  const getRecaptchaToken = useRecaptchaToken();
  const { t } = useTranslation();
  const dispatch = useDispatch<AppDispatch>();
  const user = useSelector((state: RootState) => state.auth.user);
  const { loading, error, success } = useSelector((state: RootState) => state.contactUs);
  const [isFocusedMessage, setIsFocusedMessage] = useState(false);
  
  const [formData, setFormData] = useState({
    name: '',
    phone: '+49',
    email: '',
    message: ''
  });
  const [errors, setErrors] = useState<Partial<Record<keyof typeof formData, string>>>({});
  const [checkbox, setCheckbox] = useState(false);

  useEffect(() => {
    if (user?.email) {
      setFormData(prev => ({ ...prev, email: user.email || '' }));
    }
  }, [user?.email]);

  useEffect(() => {
    if (success) {
      dispatch(addNotification({
        type: 'success',
        title: t('contact_form.form.notification_success'),
        message: t('contact_form.form.success_message'),
        autoHideDuration: 5000,
      }));
      dispatch(resetContactUs());
      // Reset form
      setFormData({ name: '', phone: '+49', email: user?.email || '', message: '' });
      setCheckbox(false);
      setErrors({});
    }
  }, [success, dispatch, t, user?.email]);

  const handleFocusMessage = () => {
    setIsFocusedMessage(true);
  };

  const handleBlurMessage = () => {
    setIsFocusedMessage(false);
  };

  useEffect(() => {
    if (error) {
      let errorTitle = t('contact_form.form.error_title');
      let errorMessage = t('contact_form.form.error_generic');

      if (error === 'captcha_failed') {
        errorMessage = t('contact_form.form.error_captcha');
      } else if (error === 'rate_limit') {
        errorMessage = t('contact_form.form.error_rate_limit');
      }

      dispatch(addNotification({
        type: 'error',
        title: errorTitle,
        message: errorMessage,
        autoHideDuration: 5000,
      }));
      dispatch(resetContactUs());
    }
  }, [error, dispatch, t]);

  const validateForm = () => {
    const newErrors: Partial<Record<keyof typeof formData | 'checkbox', string>> = {};
    
    if (!formData.name.trim()) {
      newErrors.name = t('contact_form.form.name_required');
    }
    
    if (!formData.phone.trim()) {
      newErrors.phone = t('contact_form.form.phone_required');
    }
    
    if (!formData.email.trim()) {
      newErrors.email = t('contact_form.form.email_required');
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = t('contact_form.form.email_invalid');
    }
    
    if (!formData.message.trim()) {
      newErrors.message = t('contact_form.form.message_required');
    }
    
    if (!checkbox) {
      const cardElement = document.querySelector('.contact-form-checkbox');
      if (cardElement) {
        cardElement.classList.add('shake');
        setTimeout(() => {
          cardElement.classList.remove('shake');
        }, 500);
      }
      newErrors.checkbox = t('contact_form.form.checkbox_required');
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    // Unlike auth, the contact endpoint keeps accepting a missing token -
    // preserve the old "send null on captcha failure" behavior here.
    let captchaToken: string | null = null;
    try {
      captchaToken = await getRecaptchaToken('contact_form');
    } catch (err) {
      console.error('reCAPTCHA token fetch failed:', err);
    }

    const contactData: ContactUsData = {
      fullName: formData.name,
      phone: cleanPhoneNumber(formData.phone),
      email: formData.email,
      message: formData.message,
      dataProcessingConsent: checkbox,
      captchaToken,
    };

    dispatch(sendContactUs(contactData));
  };

  const handleInputChange = (field: keyof typeof formData) => (value: string) => {
    let processedValue = value;
    
    // Применяем маску для телефона
    if (field === 'phone') {
      processedValue = formatPhoneNumber(value);
    }
    
    setFormData(prev => ({ ...prev, [field]: processedValue }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  return (
    <div className={`contact-form ${className}`}>
      <div className="contact-form-wrapper">
        <div className="contact-form-container">
          <h2 className="contact-form-title">{t('contact_form.title')}</h2>
          <p className="contact-form-description">{t('contact_form.description')}</p>
          <form className="contact-form-form" onSubmit={handleSubmit}>
            <div className="form-group">
              <Input
                id="name"
                label={t('contact_form.form.name_label')}
                value={formData.name}
                onChange={handleInputChange('name')}
                error={errors.name}
              />
            </div>

            <div className="form-group">
              <Input
                id="phone"
                label={t('contact_form.form.phone_label')}
                value={formData.phone}
                onChange={handleInputChange('phone')}
                error={errors.phone}
                placeholder="+49 123 456 789"
              />
            </div>
            
            <div className="form-group">
              <Input
                id="email"
                label={t('contact_form.form.email_label')}
                type="email"
                value={formData.email}
                onChange={handleInputChange('email')}
                error={errors.email}
              />
            </div>
            
            <div className="form-group">
              <div className="form-field">
                <label htmlFor="message" className="form-label">{t('contact_form.form.message_label')}</label>
                <div className="input-container">
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={(e) => handleInputChange('message')(e.target.value)}
                    onFocus={handleFocusMessage}
                    onBlur={handleBlurMessage}
                    className={`form-textarea ${!isFocusedMessage && errors.message ? 'error' : ''}`}
                    rows={4}
                  />
                  {!isFocusedMessage && errors.message && <div className="error-message">{errors.message}</div>}
                </div>
              </div>
            </div>

            <div className="form-group text-center contact-form-checkbox">
              <Checkbox
                children={
                  <>
                    {t('contact_form.form.checkbox_label')}
                    <a href="https://seller2go.com/datenschutzerklaerung/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="terms-link"
                    > {t('contact_form.form.checkbox_link')}</a>
                  </>
                }
                checked={checkbox}
                onChange={() => setCheckbox(!checkbox)}
              />
            </div>
            
            <button
              type="submit"
              className="contact-form-button"
              disabled={loading}
            >
              {loading ? t('contact_form.form.sending_button') : t('contact_form.form.submit_button')}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

const ContactForm: React.FC<ContactFormProps> = (props) => (
  <RecaptchaProvider>
    <ContactFormInner {...props} />
  </RecaptchaProvider>
);

export default ContactForm;
